// engine-exception: dispatch is a decision, not a list — the next-up card, its score breakdown and the workload heatmap come from two aggregate reads (/tickets/next, /tickets/workload) with accept/reject on the offer
import { useNavigate } from 'react-router-dom';
import { useAction, useApiQuery } from '../../engine/api/hooks';
import { useDataSource } from '../../engine/meta/useDataSource';
import { Badge, Chip, EmptyState, ErrorBanner, PageHeader, Skeleton } from '../../engine/design/primitives/Misc';
import { Button } from '../../engine/design/primitives/Button';
import { Icon } from '../../engine/design/icons';
import { formatMinutes } from '../../lib/format';
import { priorityTone, usePriorities, useQueues } from './lib/catalog';

// The dispatch board (docs/servicedesk-v2-proposal.md P2): the one ticket to work on next, the
// reasoning that chose it, and the desk's shape around it. Rejecting is a first-class answer —
// it puts the ticket on a cooldown rather than leaving it at the top for everyone else too.

interface Factor { key: string; label: string; points: number; detail?: string }
interface Scored { ticket_id: string; ref: string | null; summary: string | null; points: number; factors: Factor[] }
interface NextResult {
  ticket: Record<string, unknown> | null;
  score: Scored | null;
  alternatives: Scored[];
  considered: number;
  reason?: string;
}
interface Workload {
  technicians: Array<{ technician_id: string; name: string; accepts_dispatch: boolean; max_open_tickets: number | null; daily_capacity_minutes: number | null; open_tickets: number; new_tickets: number; at_risk: number; minutes_today: number }>;
  queues: Array<{ queue_id: string; name: string; unassigned: number; at_risk: number }>;
}

export function useNextTicket(queueId?: string | null) {
  const qs = queueId ? `?queue_id=${queueId}` : '';
  // Dispatch is NovaPSA's own engine: a tenant on its connected system (Halo) has none to ask.
  const native = useDataSource().overlay === null;
  return useApiQuery<NextResult>(['dispatch-next', queueId ?? 'all'], `/v1/tickets/next${qs}`, { staleTime: 0, enabled: native });
}

function ScoreBreakdown({ score }: { score: Scored }) {
  return (
    <ul className="hf-stack" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0 }} aria-label="Why this ticket">
      {score.factors.map((f) => (
        <li key={f.key} className="hf-row" style={{ gap: 'var(--s-1)', fontSize: 'var(--t-12)' }}>
          <span className="hf-num" style={{ minWidth: 48, textAlign: 'right', color: f.points < 0 ? 'var(--neg)' : 'var(--ink-2)' }}>
            {f.points > 0 ? '+' : ''}{f.points}
          </span>
          <span>{f.label}</span>
          {f.detail && <span className="hf-muted">{f.detail}</span>}
        </li>
      ))}
    </ul>
  );
}

export function DispatchScreen() {
  const navigate = useNavigate();
  const next = useNextTicket();
  const workload = useApiQuery<Workload>(['dispatch-workload'], '/v1/tickets/workload');
  const accept = useAction('tickets', 'accept', { toast: { success: 'It is yours' } });
  const reject = useAction('tickets', 'reject', { toast: { success: 'Skipped for now' } });
  const priorities = usePriorities();
  const queues = useQueues();

  if (next.error) return <ErrorBanner error={next.error} />;
  const offer = next.data;
  const ticket = offer?.ticket as Record<string, unknown> | null | undefined;

  const refresh = () => {
    void next.refetch();
    void workload.refetch();
  };

  return (
    <div className="hf-page">
      <PageHeader
        title="Dispatch"
        subtitle="One ticket at a time, chosen by the desk's own rules — with the reasoning shown."
        actions={<Button size="sm" variant="ghost" icon="history" onClick={refresh}>Re-check</Button>}
      />

      <section className="hf-card pad hf-stack" aria-label="Next ticket">
        {next.isLoading ? (
          <Skeleton lines={4} />
        ) : !ticket || !offer?.score ? (
          <EmptyState title={offer?.reason ?? 'Nothing waiting for you'} icon="check" />
        ) : (
          <>
            <div className="hf-between">
              <div className="hf-stack" style={{ gap: 2 }}>
                <span className="hf-row" style={{ gap: 'var(--s-1)' }}>
                  <span className="hf-mono hf-muted">{String(ticket['ref'] ?? '')}</span>
                  {ticket['priority_id'] ? (
                    <Badge tone={priorityTone(priorities.byId.get(String(ticket['priority_id']))?.level)}>
                      {priorities.byId.get(String(ticket['priority_id']))?.name ?? 'Priority'}
                    </Badge>
                  ) : null}
                  {ticket['queue_id'] ? <Chip icon="inbox">{queues.byId.get(String(ticket['queue_id']))?.name ?? 'Queue'}</Chip> : null}
                </span>
                <h2 style={{ margin: 0, fontSize: 'var(--t-18)' }}>{String(ticket['summary'] ?? '(no summary)')}</h2>
              </div>
              <Badge tone="accent" title="Dispatch score">
                <span className="hf-num">{offer.score.points} pts</span>
              </Badge>
            </div>

            <ScoreBreakdown score={offer.score} />

            <div className="hf-row">
              <Button
                variant="primary"
                icon="check"
                busy={accept.isPending}
                onClick={() => accept.mutate({ id: offer.score!.ticket_id, body: {} }, { onSuccess: () => navigate(`/tickets/${offer.score!.ticket_id}`) })}
              >
                Take it
              </Button>
              <Button
                variant="ghost"
                icon="x"
                busy={reject.isPending}
                onClick={() => reject.mutate({ id: offer.score!.ticket_id, body: { reason: 'Not me right now' } }, { onSuccess: refresh })}
              >
                Not me
              </Button>
              <Button variant="link" onClick={() => navigate(`/tickets/${offer.score!.ticket_id}`)}>Just open it</Button>
              <span className="hf-grow" />
              <span className="hf-muted" style={{ fontSize: 'var(--t-12)' }}>{offer.considered} considered</span>
            </div>

            {offer.alternatives.length > 0 && (
              <div className="hf-stack" style={{ gap: 'var(--s-1)' }}>
                <div className="hf-caps">Next in line</div>
                <ul className="hf-stack" style={{ gap: 2, listStyle: 'none', padding: 0, margin: 0 }}>
                  {offer.alternatives.map((a) => (
                    <li key={a.ticket_id} className="hf-row" style={{ gap: 'var(--s-1)', fontSize: 'var(--t-12)' }}>
                      <span className="hf-num" style={{ minWidth: 48, textAlign: 'right' }}>{a.points}</span>
                      <Button variant="link" size="sm" onClick={() => navigate(`/tickets/${a.ticket_id}`)}>{a.ref}</Button>
                      <span className="hf-muted">{a.summary}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      <section className="hf-stack" style={{ gap: 'var(--s-2)', marginTop: 'var(--s-4)' }} aria-label="Workload">
        <h2 className="hf-section-title">Who is carrying what</h2>
        {workload.isLoading ? (
          <Skeleton lines={5} />
        ) : (
          <div className="hf-stack" style={{ gap: 'var(--s-1)' }}>
            {(workload.data?.technicians ?? []).map((t) => {
              const ceiling = t.max_open_tickets;
              const full = ceiling !== null && t.open_tickets >= ceiling;
              return (
                <div key={t.technician_id} className="hf-row" style={{ gap: 'var(--s-2)' }}>
                  <span style={{ minWidth: 160 }}>{t.name}</span>
                  <Badge tone={full ? 'neg' : t.open_tickets === 0 ? 'muted' : 'accent'} dot={false}>
                    {t.open_tickets}{ceiling !== null ? ` / ${ceiling}` : ''} open
                  </Badge>
                  {t.at_risk > 0 && <Badge tone="warn">{t.at_risk} at risk</Badge>}
                  {!t.accepts_dispatch && <Badge tone="muted" dot={false}>off the desk</Badge>}
                  <span className="hf-muted hf-num" style={{ fontSize: 'var(--t-12)' }}>
                    {formatMinutes(t.minutes_today)} today{t.daily_capacity_minutes ? ` of ${formatMinutes(t.daily_capacity_minutes)}` : ''}
                  </span>
                </div>
              );
            })}
          </div>
        )}

        <h2 className="hf-section-title">Nobody has picked up</h2>
        <div className="hf-chiprow">
          {(workload.data?.queues ?? []).map((q) => (
            <Chip key={q.queue_id} icon="inbox" count={q.unassigned} onClick={() => navigate(`/tickets/board?queue=${q.queue_id}`)}>
              {q.name}
              {q.at_risk > 0 && <span className="hf-muted"> · {q.at_risk} at risk</span>}
            </Chip>
          ))}
          {(workload.data?.queues ?? []).length === 0 && <span className="hf-muted"><Icon name="check" size={12} /> every queue is clear</span>}
        </div>
      </section>
    </div>
  );
}
